import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';

import Banner1 from '../../assets/img/website-images/Blogdetailsbanner1.png';

/* ─── Variants ─────────────────────────────────────────────────── */
const heroVariants = {
    hidden: { opacity: 0, scale: 1.04 },
    visible: {
        opacity: 1, scale: 1,
        transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
    },
};

const titleVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
    },
};

const paraStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.16, delayChildren: 0.1 } },
};

const paraVariant = {
    hidden: { opacity: 0, y: 28 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.65, ease: 'easeOut' },
    },
};

/* ─── Paragraph with individual scroll trigger ──────────────────── */
function AnimPara({ children, delay = 0 }) {
    const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
    return (
        <motion.p
            ref={ref}
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, ease: 'easeOut', delay }}
        >
            {children}
        </motion.p>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function BlogOne() {
    /* Hero image */
    const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* Title */
    const { ref: titleRef, inView: titleInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: hero image reveal with clip-path wipe */
    const imgWrapRef = useRef(null);
    useEffect(() => {
        if (heroInView && imgWrapRef.current) {
            gsap.fromTo(
                imgWrapRef.current,
                { clipPath: 'inset(0 100% 0 0)', opacity: 1 },
                { clipPath: 'inset(0 0% 0 0)', duration: 1.1, ease: 'power3.inOut', delay: 0.1 }
            );
        }
    }, [heroInView]);

    /* GSAP: subtle parallax on hero image while scrolling */
    const heroImgRef = useRef(null);
    useEffect(() => {
        const handleScroll = () => {
            if (!heroImgRef.current) return;
            const rect = heroImgRef.current.getBoundingClientRect();
            const progress = 1 - (rect.bottom / (window.innerHeight + rect.height));
            gsap.set(heroImgRef.current, { y: progress * 30 });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    /* GSAP: decorative reading-progress bar at top */
    const progressRef = useRef(null);
    useEffect(() => {
        const handleScroll = () => {
            if (!progressRef.current) return;
            const scrollTop    = window.scrollY;
            const docHeight    = document.body.scrollHeight - window.innerHeight;
            const progress     = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
            gsap.set(progressRef.current, { width: `${progress}%` });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            {/* Reading progress bar */}
            <div style={{
                position: 'fixed', top: 0, left: 0, height: '3px',
                background: 'transparent', zIndex: 9999, width: '100%',
                pointerEvents: 'none',
            }}>
                <div
                    ref={progressRef}
                    style={{
                        height: '100%', width: '0%',
                        background: 'linear-gradient(90deg, #1E5AA8, #F27024)',
                        borderRadius: '0 2px 2px 0',
                        transition: 'width 0.1s linear',
                    }}
                />
            </div>

            <section className="project-detail">
                <div className="container">

                    {/* ── Hero Image ── */}
                    <div className="upper-box" ref={heroRef}>
                        <motion.div
                            variants={heroVariants}
                            initial="hidden"
                            animate={heroInView ? 'visible' : 'hidden'}
                            style={{ borderRadius: '12px', overflow: 'hidden' }}
                        >
                            <div ref={imgWrapRef} style={{ overflow: 'hidden', borderRadius: '12px' }}>
                                <figure className="image" style={{ margin: 0, overflow: 'hidden' }}>
                                    <img
                                        ref={heroImgRef}
                                        src={Banner1}
                                        alt="Blog Banner"
                                        style={{ display: 'block', width: '100%', transformOrigin: 'center' }}
                                    />
                                </figure>
                            </div>
                        </motion.div>
                    </div>

                    {/* ── Article Content ── */}
                    <div className="lower-content2">
                        <div className="row">
                            <div className="text-column col-lg-12 col-md-12 col-sm-12">
                                <div className="s-about-content">

                                    {/* Title */}
                                    <motion.h2
                                        ref={titleRef}
                                        variants={titleVariants}
                                        initial="hidden"
                                        animate={titleInView ? 'visible' : 'hidden'}
                                    >
                                        Top Career Opportunities After B.Pharm: What You Can Become
                                    </motion.h2>

                                    {/* Paragraphs — each triggers individually on scroll */}
                                    <AnimPara delay={0}>
                                        Completing a Bachelor of Pharmacy (B.Pharm) is more than earning a degree — it is the
                                        beginning of a dynamic career journey in one of the world's most essential industries.
                                        Pharmaceutical sciences sit at the intersection of chemistry, biology, healthcare, and
                                        innovation. As a B.Pharm graduate, you are equipped to contribute meaningfully to
                                        society in a variety of ways.
                                    </AnimPara>

                                    <AnimPara delay={0}>
                                        One of the most traditional and respected paths is becoming a licensed <strong>Pharmacist</strong> —
                                        working in hospitals, retail pharmacies, or community health centres. As a pharmacist,
                                        you serve as the last line of defence before a patient receives medication, ensuring
                                        the right drug, dose, and instructions reach the right person. Beyond this,
                                        <strong> Clinical Pharmacists</strong> work directly alongside doctors in hospitals to review
                                        prescriptions, monitor drug interactions, and advise on therapeutic choices.
                                    </AnimPara>

                                    <AnimPara delay={0}>
                                        For those drawn to science and research, roles in <strong>Formulation Development</strong> and
                                        <strong> Analytical Research</strong> allow graduates to work on creating and testing new drug
                                        products. The pharmaceutical manufacturing sector employs B.Pharm graduates as
                                        <strong> Production Officers</strong> and <strong>Quality Control (QC) / Quality Assurance (QA) Analysts</strong>,
                                        ensuring every medicine that reaches a patient meets the highest standards of safety
                                        and efficacy.
                                    </AnimPara>

                                    <AnimPara delay={0}>
                                        The growing field of <strong>Clinical Research</strong> offers exciting opportunities for graduates
                                        to work on drug trials, data analysis, and patient safety monitoring.
                                        <strong> Regulatory Affairs Specialists</strong> and <strong>Drug Safety Associates</strong> play a critical
                                        role in getting medicines approved and keeping them safe once they reach the market.
                                        <strong> Medical Writers</strong> and <strong>Drug Information Specialists</strong> combine scientific
                                        knowledge with communication skills to produce everything from patient leaflets to
                                        clinical study reports.
                                    </AnimPara>

                                    <AnimPara delay={0}>
                                        At Akash Institute of Pharmacy, our curriculum is designed with all these career
                                        paths in mind. We ensure every student graduates with not just theoretical knowledge,
                                        but also the practical skills, industry exposure, and professional mindset to thrive
                                        in their chosen field. The pharmaceutical industry is growing at a rapid pace — and
                                        with a B.Pharm from AIP, you are ready to grow with it.
                                    </AnimPara>

                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>
        </>
    );
}

export default BlogOne;
